import { FC, useEffect, useRef, useState } from "react";
import classes from './calenarFilters.module.scss'
import { useAppSelector } from "../../../app/store/store";
import { useMyActions } from "../../../entities/my";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { calendarService, ICalendarFilter, IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { IItem } from "../../../shared/model/types";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { ToggleSwitch } from "../../../shared/ui/toggleSwitch";
import { SurfaceBlock } from "../../../shared/ui/surfaceBlock";
import { MyButton } from "../../../shared/ui/button";
import { getSearchParamsFromCalendarFilter, isCalendarFilterComplete } from "../model/calendarSearchParams";
import { formatTutorLoad } from "../../../shared/lib/helpers/formatTutorLoad";

interface IProps {
    filter: ICalendarFilter;
    onChange: (filter: ICalendarFilter, changedField?: TFilterIdsField | TFilterBooleanField) => void;
    onValidated: (filter: ICalendarFilter | null, options?: IGetCalendarFilterOptionsResponse | null) => void;
}

type TFilterIdsField = 'tgAccountIds' | 'userIds' | 'studentIds' | 'tagIds'
type TFilterBooleanField = 'includeCancelled' | 'includeRescheduled'
type TStage = TFilterIdsField | null

const FILTER_STAGE_ORDER: TFilterIdsField[] = [
    'tgAccountIds',
    'userIds',
    'studentIds',
    'tagIds',
]

type TFilterOption = {
    id: string;
}

const normalizeFilter = (filter: ICalendarFilter): ICalendarFilter => ({
    tgAccountIds: filter.tgAccountIds || [],
    userIds: filter.userIds || [],
    studentIds: filter.studentIds || [],
    tagIds: filter.tagIds || [],
    includeCancelled: Boolean(filter.includeCancelled),
    includeRescheduled: Boolean(filter.includeRescheduled),
})

const normalizeOptions = (
    response: IGetCalendarFilterOptionsResponse
): IGetCalendarFilterOptionsResponse => ({
    viewer: response.viewer,
    tgAccounts: response.tgAccounts || [],
    tutors: response.tutors || [],
    students: response.students || [],
    tags: response.tags || [],
})

const toItems = <T extends TFilterOption>(options: T[], getName: (option: T) => string): IItem<string>[] => {
    return options.map(option => ({id: option.id, name: getName(option)}))
}

const getFilterKey = (filter: ICalendarFilter): string => {
    return getSearchParamsFromCalendarFilter(normalizeFilter(filter)).toString()
}

const getExistingIds = (ids: string[], options: TFilterOption[]): string[] => {
    const existingIds = new Set(options.map(option => option.id))

    return [...new Set(ids.filter(id => existingIds.has(id)))]
}

const hasSameIds = (selectedIds: string[], existingIds: string[]): boolean => {
    return selectedIds.length === existingIds.length &&
        selectedIds.every(id => existingIds.includes(id))
}

const withoutSelectedIds = (filter: ICalendarFilter): ICalendarFilter => {
    const normalizedFilter = normalizeFilter(filter)

    return {
        ...normalizedFilter,
        tgAccountIds: [],
        userIds: [],
        studentIds: [],
        tagIds: [],
    }
}

const getDefaultFilter = (): ICalendarFilter => ({
    tgAccountIds: [],
    userIds: [],
    studentIds: [],
    tagIds: [],
    includeCancelled: true,
    includeRescheduled: true,
})

const getNextStage = (field: TFilterIdsField): TStage => {
    if(field === 'tgAccountIds') return 'userIds'
    if(field === 'userIds') return 'studentIds'
    if(field === 'studentIds') return 'tagIds'

    return null
}

const isAbortError = (error: unknown): boolean => {
    return error instanceof DOMException && error.name === 'AbortError'
}

export const CalendarFilters: FC<IProps> = ({filter, onChange, onValidated}) => {

    const [stage, setStage] = useState<TStage>('tgAccountIds')
    const [visibleStage, setVisibleStage] = useState<TFilterIdsField>('tgAccountIds')
    const [options, setOptions] = useState<IGetCalendarFilterOptionsResponse | null>(null)
    const [fullFilter, setFullFilter] = useState<ICalendarFilter | null>(null)
    const [isSelectingAll, setIsSelectingAll] = useState(false)
    const [tgAccountOptions, setTgAccountOptions] = useState<IGetCalendarFilterOptionsResponse['tgAccounts']>([])
    const [tutorOptions, setTutorOptions] = useState<IGetCalendarFilterOptionsResponse['tutors']>([])
    const [studentOptions, setStudentOptions] = useState<IGetCalendarFilterOptionsResponse['students']>([])
    const [tagOptions, setTagOptions] = useState<IGetCalendarFilterOptionsResponse['tags']>([])
    const tgAccountOptionsRef = useRef<IGetCalendarFilterOptionsResponse['tgAccounts']>([])
    const tutorOptionsRef = useRef<IGetCalendarFilterOptionsResponse['tutors']>([])
    const studentOptionsRef = useRef<IGetCalendarFilterOptionsResponse['students']>([])
    const tagOptionsRef = useRef<IGetCalendarFilterOptionsResponse['tags']>([])
    const abortControllerRef = useRef<AbortController | null>(null)
    const skipValidationForFilterKey = useRef<string | null>(null)

    const {my} = useAppSelector(state => state.myReducer)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const getCurrentTutorUserId = (
        tutors: IGetCalendarFilterOptionsResponse['tutors'],
        selectedUserIds: string[],
    ): string | null => {
        if(my.role !== 'tutor' || selectedUserIds.length) return null

        const currentUserId = String(my.id)

        return tutors.some(tutor => tutor.userId === currentUserId)
            ? currentUserId
            : null
    }

    const showRequestError = (error: unknown) => {
        console.log(error)

        if(error instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: error.message, type: 'error'})
        }
        else if(error instanceof MyError){
            setGlobalMessage({message: error.message, type: 'error'})
        }
        else{
            setGlobalMessage({message: 'Ошибка при получении фильтров', type: 'error'})
        }
    }

    const requestFilterOptions = async (selected: ICalendarFilter, signal: AbortSignal) => {
        const response = await calendarService.getCalendarFilterOptions({selected: normalizeFilter(selected)}, signal)
        return normalizeOptions(response)
    }

    const updateTgAccountOptions = (items: IGetCalendarFilterOptionsResponse['tgAccounts']) => {
        tgAccountOptionsRef.current = items
        setTgAccountOptions(items)
    }

    const updateTutorOptions = (items: IGetCalendarFilterOptionsResponse['tutors']) => {
        tutorOptionsRef.current = items
        setTutorOptions(items)
    }

    const updateStudentOptions = (items: IGetCalendarFilterOptionsResponse['students']) => {
        studentOptionsRef.current = items
        setStudentOptions(items)
    }

    const updateTagOptions = (items: IGetCalendarFilterOptionsResponse['tags']) => {
        tagOptionsRef.current = items
        setTagOptions(items)
    }

    const setOptionsForStage = (
        response: IGetCalendarFilterOptionsResponse,
        nextStage: TStage
    ): IGetCalendarFilterOptionsResponse => {
        if(nextStage === 'tgAccountIds'){
            updateTgAccountOptions(response.tgAccounts)
            updateTutorOptions(response.tutors)
            updateStudentOptions(response.students)
            updateTagOptions(response.tags)

            return response
        }

        if(nextStage === 'userIds'){
            updateTutorOptions(response.tutors)
            updateStudentOptions(response.students)
            updateTagOptions(response.tags)

            return {
                ...response,
                tgAccounts: tgAccountOptionsRef.current.length ? tgAccountOptionsRef.current : response.tgAccounts,
            }
        }

        if(nextStage === 'studentIds'){
            updateStudentOptions(response.students)
            updateTagOptions(response.tags)

            return {
                ...response,
                tgAccounts: tgAccountOptionsRef.current.length ? tgAccountOptionsRef.current : response.tgAccounts,
                tutors: tutorOptionsRef.current.length ? tutorOptionsRef.current : response.tutors,
            }
        }

        if(nextStage === 'tagIds'){
            updateTagOptions(response.tags)

            return {
                ...response,
                tgAccounts: tgAccountOptionsRef.current.length ? tgAccountOptionsRef.current : response.tgAccounts,
                tutors: tutorOptionsRef.current.length ? tutorOptionsRef.current : response.tutors,
                students: studentOptionsRef.current.length ? studentOptionsRef.current : response.students,
            }
        }

        return {
            ...response,
            tgAccounts: tgAccountOptionsRef.current.length ? tgAccountOptionsRef.current : response.tgAccounts,
            tutors: tutorOptionsRef.current.length ? tutorOptionsRef.current : response.tutors,
            students: studentOptionsRef.current.length ? studentOptionsRef.current : response.students,
            tags: (tagOptionsRef.current?.length ? tagOptionsRef.current : response.tags) || [],
        }
    }

    const createAbortController = (): AbortController => {
        abortControllerRef.current?.abort()

        const controller = new AbortController()
        abortControllerRef.current = controller

        return controller
    }

    const getData = async (selected: ICalendarFilter, nextStage: TStage) => {
        const controller = createAbortController()

        try{
            const normalizedSelected = normalizeFilter(selected)
            if(nextStage) setVisibleStage(nextStage)
            setStage(nextStage)
            const response = await requestFilterOptions(normalizedSelected, controller.signal)

            if(!controller.signal.aborted){
                const nextOptions = setOptionsForStage(response, nextStage)
                setOptions(nextOptions)

                // Tags refine an already valid TG → tutor → student filter. The
                // calendar must be available while their options are loading.
                if(nextStage === 'tagIds' && isCalendarFilterComplete(normalizedSelected)){
                    onValidated(normalizedSelected, nextOptions)
                }

                if(nextStage === 'userIds'){
                    const currentTutorUserId = getCurrentTutorUserId(response.tutors, normalizedSelected.userIds)

                    if(currentTutorUserId){
                        const nextFilter = {...normalizedSelected, userIds: [currentTutorUserId]}

                        changeFilter(nextFilter, 'userIds')
                        onValidated(null)
                        await getData(nextFilter, 'studentIds')
                    }
                }
            }
        }
        catch(error){
            if(isAbortError(error)) return

            onValidated(null)
            showRequestError(error)
        }
        finally{
            if(abortControllerRef.current === controller){
                setStage(null)
            }
        }
    }

    const resetInvalidUrlStage = (filterBeforeInvalidStage: ICalendarFilter, currentOptions: IGetCalendarFilterOptionsResponse) => {

        const filterKey = getFilterKey(filterBeforeInvalidStage)

        setOptions(currentOptions)
        skipValidationForFilterKey.current = filterKey
        onChange(filterBeforeInvalidStage)
        onValidated(null)
        setGlobalMessage({
            message: 'Ссылка больше не актуальна. Выберите фильтры заново',
            type: 'error',
        })
    }

    const validateFilterFromUrl = async (filterFromUrl: ICalendarFilter) => {
        const controller = createAbortController()
        const normalizedFilterFromUrl = normalizeFilter(filterFromUrl)

        const requestOptions = async (selected: ICalendarFilter, currentStage: Exclude<TStage, null>) => { 
            setVisibleStage(currentStage)
            setStage(currentStage)
            const response = await requestFilterOptions(selected, controller.signal)
            if(!controller.signal.aborted){
                setOptions(setOptionsForStage(response, currentStage))
            }

            return controller.signal.aborted ? null : response
        }

        try{
            onValidated(null)

            let validatedFilter = withoutSelectedIds(normalizedFilterFromUrl)
            let validatedOptions = await requestOptions(validatedFilter, 'tgAccountIds')

            if(!validatedOptions) return

            const existingTgAccountIds = getExistingIds(
                normalizedFilterFromUrl.tgAccountIds,
                validatedOptions.tgAccounts
            )

            if(!hasSameIds(normalizedFilterFromUrl.tgAccountIds, existingTgAccountIds)){
                resetInvalidUrlStage(validatedFilter, validatedOptions)
                return
            }

            validatedFilter = {...validatedFilter, tgAccountIds: existingTgAccountIds}

            if(validatedFilter.tgAccountIds.length){
                validatedOptions = await requestOptions(validatedFilter, 'userIds')
                if(!validatedOptions) return

                let existingUserIds = getExistingIds(
                    normalizedFilterFromUrl.userIds,
                    validatedOptions.tutors.map(tutor => ({id: tutor.userId}))
                )
                const currentTutorUserId = getCurrentTutorUserId(
                    validatedOptions.tutors,
                    normalizedFilterFromUrl.userIds,
                )

                if(currentTutorUserId){
                    existingUserIds = [currentTutorUserId]
                }

                if(normalizedFilterFromUrl.userIds.length && !hasSameIds(normalizedFilterFromUrl.userIds, existingUserIds)){
                    resetInvalidUrlStage(validatedFilter, validatedOptions)
                    return
                }

                validatedFilter = {...validatedFilter, userIds: existingUserIds}
            }

            if(validatedFilter.userIds.length){
                validatedOptions = await requestOptions(validatedFilter, 'studentIds')
                if(!validatedOptions) return

                const existingStudentIds = getExistingIds(
                    normalizedFilterFromUrl.studentIds,
                    validatedOptions.students
                )

                if(!hasSameIds(normalizedFilterFromUrl.studentIds, existingStudentIds)){
                    resetInvalidUrlStage(validatedFilter, validatedOptions)
                    return
                }

                validatedFilter = {...validatedFilter, studentIds: existingStudentIds}
            }

            if(validatedFilter.studentIds.length){
                validatedOptions = await requestOptions(validatedFilter, 'tagIds')
                if(!validatedOptions) return

                const existingTagIds = getExistingIds(
                    normalizedFilterFromUrl.tagIds,
                    validatedOptions.tags || []
                )

                if(!hasSameIds(normalizedFilterFromUrl.tagIds, existingTagIds)){
                    resetInvalidUrlStage(validatedFilter, validatedOptions)
                    return
                }

                validatedFilter = {...validatedFilter, tagIds: existingTagIds}
            }

            setOptions(setOptionsForStage(validatedOptions, null))

            const filterFromUrlKey = getFilterKey(normalizedFilterFromUrl)
            const validatedFilterKey = getFilterKey(validatedFilter)

            if(filterFromUrlKey !== validatedFilterKey){
                skipValidationForFilterKey.current = validatedFilterKey
                onChange(validatedFilter)
            }

            onValidated(
                isCalendarFilterComplete(validatedFilter)
                    ? validatedFilter
                    : null,
                validatedOptions
            )
        }
        catch(error){
            if(isAbortError(error)) return

            onValidated(null)
            showRequestError(error)
        }
        finally{
            if(abortControllerRef.current === controller){
                setStage(null)
            }
        }
    }

    const filterKey = getFilterKey(filter)

    useEffect(() => {
        if(skipValidationForFilterKey.current === filterKey){
            skipValidationForFilterKey.current = null
            return
        }

        validateFilterFromUrl(filter)
        // Эффект запускается по сериализованному значению фильтра из URL.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey, my.id, my.role])

    useEffect(() => {
        const currentController = abortControllerRef

        return () => {
            currentController.current?.abort()
        }
    }, [])

    const getNextFilter = (
        currentFilter: ICalendarFilter,
        field: TFilterIdsField,
        ids: string[]
    ): ICalendarFilter => {
        if(field === 'tgAccountIds'){
            return {...currentFilter, tgAccountIds: ids, userIds: [], studentIds: [], tagIds: []}
        }
        if(field === 'userIds'){
            return {...currentFilter, userIds: ids, studentIds: [], tagIds: []}
        }
        if(field === 'studentIds'){
            return {...currentFilter, studentIds: ids, tagIds: []}
        }
        return {...currentFilter, tagIds: ids}
    }

    const clearDependentOptions = (field: TFilterIdsField) => {
        abortControllerRef.current?.abort()
        setVisibleStage(field)
        setStage(null)

        setOptions(currentOptions => {
            if(!currentOptions) return currentOptions

            if(field === 'tgAccountIds'){
                updateTutorOptions([])
                updateStudentOptions([])
                updateTagOptions([])
                return {...currentOptions, tutors: [], students: [], tags: []}
            }
            if(field === 'userIds'){
                updateStudentOptions([])
                updateTagOptions([])
                return {...currentOptions, students: [], tags: []}
            }
            if(field === 'studentIds'){
                updateTagOptions([])
                return {...currentOptions, tags: []}
            }

            return currentOptions
        })
    }

    const changeFilter = (
        nextFilter: ICalendarFilter,
        changedField?: TFilterIdsField | TFilterBooleanField
    ) => {
        abortControllerRef.current?.abort()
        skipValidationForFilterKey.current = getFilterKey(nextFilter)
        onChange(nextFilter, changedField)
    }

    const onSelected = (field: TFilterIdsField) => {
        return (item: IItem<string>) => {
            return (selected: boolean) => {
                const normalizedFilter = normalizeFilter(filter)
                const currentIds = normalizedFilter[field]
                const ids = selected
                    ? [...currentIds, item.id]
                    : currentIds.filter(id => id !== item.id)

                const nextFilter = getNextFilter(normalizedFilter, field, ids)

                changeFilter(nextFilter, field)
                onValidated(null)

                if(field === 'tagIds'){
                    onValidated(isCalendarFilterComplete(nextFilter) ? nextFilter : null, options)
                    return
                }

                if(!ids.length){
                    clearDependentOptions(field)
                    return
                }

                if(field === 'studentIds'){
                    onValidated(isCalendarFilterComplete(nextFilter) ? nextFilter : null, options)
                }

                getData(nextFilter, getNextStage(field))
            }
        }
    }

    const onToggle = (field: TFilterBooleanField) => {
        return (value: boolean) => {
            const nextFilter = {...normalizeFilter(filter), [field]: value}

            changeFilter(nextFilter, field)
            onValidated(isCalendarFilterComplete(nextFilter) ? nextFilter : null, options)
        }
    }

    const onSelectAll = (field: TFilterIdsField) => {
        return (ids: string[]) => {
            const normalizedFilter = normalizeFilter(filter)
            const nextFilter = getNextFilter(normalizedFilter, field, ids)

            changeFilter(nextFilter, field)
            onValidated(null)

                if(field === 'tagIds'){
                    onValidated(isCalendarFilterComplete(nextFilter) ? nextFilter : null, options)
                    return
            }

                if(!ids.length){
                    clearDependentOptions(field)
                    return
                }

                if(field === 'studentIds'){
                    onValidated(isCalendarFilterComplete(nextFilter) ? nextFilter : null, options)
                }

                getData(nextFilter, getNextStage(field))
        }
    }

    const normalizedFilter = normalizeFilter(filter)
    const tgAccounts = tgAccountOptions.length ? tgAccountOptions : options?.tgAccounts ?? []
    const tutors = tutorOptions.length ? tutorOptions : options?.tutors ?? []
    const students = studentOptions.length ? studentOptions : options?.students ?? []
    const tags = (tagOptions?.length ? tagOptions : (options?.tags ?? [])) || []

    const tgAccountItems = toItems(tgAccounts, tg => tg.title || `@${tg.username}`)
    const tutorItems = tutors.map(tutor => ({id: tutor.userId, name: tutor.fullName, secondaryText: formatTutorLoad(tutor)}))
    const studentItems = toItems(students, student => student.fullName)
    const tagItems = toItems(tags, tag => tag.name)

    const isStageVisible = (filterStage: TFilterIdsField): boolean => {
        return FILTER_STAGE_ORDER.indexOf(filterStage) <= FILTER_STAGE_ORDER.indexOf(visibleStage)
    }

    const resetFilter = () => {
        const nextFilter = getDefaultFilter()

        setFullFilter(null)
        clearDependentOptions('tgAccountIds')
        changeFilter(nextFilter)
        onValidated(null)
    }

    const selectAllFilters = async () => {
        if(stage !== null || isSelectingAll) return

        const controller = createAbortController()
        setIsSelectingAll(true)

        try{
            const emptyFilter = getDefaultFilter()
            const initialOptions = await requestFilterOptions(emptyFilter, controller.signal)
            if(controller.signal.aborted) return

            const tgAccountIds = initialOptions.tgAccounts.map(item => item.id)
            const filterWithTg: ICalendarFilter = {
                ...emptyFilter,
                tgAccountIds,
            }
            const optionsByTg = tgAccountIds.length
                ? await requestFilterOptions(filterWithTg, controller.signal)
                : initialOptions
            if(controller.signal.aborted) return

            const userIds = optionsByTg.tutors.map(item => item.userId)
            const filterWithTutors: ICalendarFilter = {
                ...filterWithTg,
                userIds,
            }
            const optionsByTutors = userIds.length
                ? await requestFilterOptions(filterWithTutors, controller.signal)
                : {...optionsByTg, students: [], tags: []}
            if(controller.signal.aborted) return

            const studentIds = optionsByTutors.students.map(item => item.id)
            const filterWithStudents: ICalendarFilter = {
                ...filterWithTutors,
                studentIds,
            }
            const optionsByStudents = studentIds.length
                ? await requestFilterOptions(filterWithStudents, controller.signal)
                : {...optionsByTutors, tags: []}
            if(controller.signal.aborted) return

            const fullFilter: ICalendarFilter = {
                ...filterWithStudents,
                // An empty tag filter includes both tagged and untagged lessons.
                tagIds: [],
                includeCancelled: true,
                includeRescheduled: true,
            }
            const fullOptions = setOptionsForStage({
                ...optionsByStudents,
                tgAccounts: initialOptions.tgAccounts,
                tutors: optionsByTg.tutors,
                students: optionsByTutors.students,
                tags: optionsByStudents.tags || [],
            }, null)

            setOptions(fullOptions)
            setFullFilter(fullFilter)
            setVisibleStage('tagIds')
            skipValidationForFilterKey.current = getFilterKey(fullFilter)
            changeFilter(fullFilter)
            onValidated(isCalendarFilterComplete(fullFilter) ? fullFilter : null, fullOptions)
        }
        catch(error){
            if(isAbortError(error)) return

            onValidated(null)
            showRequestError(error)
        }
        finally{
            if(abortControllerRef.current === controller){
                setStage(null)
                setIsSelectingAll(false)
            }
        }
    }

    const isFullFilterSelected = Boolean(fullFilter)
        && hasSameIds(normalizedFilter.tgAccountIds, fullFilter?.tgAccountIds || [])
        && hasSameIds(normalizedFilter.userIds, fullFilter?.userIds || [])
        && hasSameIds(normalizedFilter.studentIds, fullFilter?.studentIds || [])
        && hasSameIds(normalizedFilter.tagIds, fullFilter?.tagIds || [])
        && normalizedFilter.includeCancelled === fullFilter?.includeCancelled
        && normalizedFilter.includeRescheduled === fullFilter?.includeRescheduled

    return (
        <SurfaceBlock className={classes.container}>
            <section className={classes.actions}>
                <section className={classes.showAllButton}>
                    <MyButton
                        isLoading={stage !== null || isSelectingAll}
                        onClick={() => {
                            if(isFullFilterSelected) return
                            selectAllFilters()
                        }}
                    >
                        Показать все занятия
                    </MyButton>
                </section>
                <section className={classes.resetButton}>
                    <MyButton onClick={resetFilter}>
                        Сбросить фильтры
                    </MyButton>
                </section>
            </section>

            <section className={classes.mainFilters}>
                <section className={classes.field}>
                    <span className={classes.label}>Рабочие Telegram</span>
                    <DropDownListSelected
                        isLoading={stage === 'tgAccountIds'}
                        items={tgAccountItems}
                        selectedIdItems={normalizedFilter.tgAccountIds}
                        onSelected={onSelected('tgAccountIds')}
                        onSelectAll={onSelectAll('tgAccountIds')}
                        selectedCount
                        search
                        searchPlaceholder="Поиск рабочего Telegram"
                    />
                </section>

                {Boolean(normalizedFilter.tgAccountIds.length) && isStageVisible('userIds') && (
                    <section className={classes.field}>
                        <span className={classes.label}>Исполнители</span>
                        <DropDownListSelected
                            isLoading={stage === 'userIds'}
                            items={tutorItems}
                            selectedIdItems={normalizedFilter.userIds}
                            onSelected={onSelected('userIds')}
                            onSelectAll={onSelectAll('userIds')}
                            selectedCount
                            search
                            searchPlaceholder="Поиск исполнителя"
                        />
                    </section>
                )}

                {Boolean(normalizedFilter.userIds.length) && isStageVisible('studentIds') && (
                    <section className={classes.field}>
                        <span className={classes.label}>Ученики</span>
                        <DropDownListSelected
                            isLoading={stage === 'studentIds'}
                            items={studentItems}
                            selectedIdItems={normalizedFilter.studentIds}
                            onSelected={onSelected('studentIds')}
                            onSelectAll={onSelectAll('studentIds')}
                            selectedCount
                            search
                            searchPlaceholder="Поиск ученика"
                        />
                    </section>
                )}

                {Boolean(normalizedFilter.studentIds.length) && isStageVisible('tagIds') && (
                    <section className={classes.field}>
                        <span className={classes.label}>Теги</span>
                        <DropDownListSelected
                            isLoading={stage === 'tagIds'}
                            items={tagItems}
                            selectedIdItems={normalizedFilter.tagIds}
                            onSelected={onSelected('tagIds')}
                            onSelectAll={onSelectAll('tagIds')}
                            selectedCount
                            search
                            searchPlaceholder="Поиск тега"
                        />
                    </section>
                )}
            </section>

            <section className={classes.statusFilters}>
                <span className={classes.statusTitle}>Показывать в календаре</span>
                <section className={classes.toggles}>
                    <section className={classes.toggle}>
                        <ToggleSwitch
                            label="Отменённые занятия"
                            checked={normalizedFilter.includeCancelled}
                            onSelected={onToggle('includeCancelled')}
                        />
                    </section>
                    <section className={classes.toggle}>
                        <ToggleSwitch
                            label="Перенесённые занятия"
                            checked={normalizedFilter.includeRescheduled}
                            onSelected={onToggle('includeRescheduled')}
                        />
                    </section>
                </section>
            </section>
        </SurfaceBlock>
    )
}
